import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import '../ns-form/ns-form.js';
import {CITY_STATE_ZIP_TRANSLATIONS} from './city-state-zip-translations.js';
import template from './ns-city-state-zip.html';
import {ShippingService} from '@nuskin/ns-shop';
import {CITY_STATE_ZIP_CONFIG} from './city-state-zip-config.js';
import {STATE_COUNTY} from './state-county-data.js';
import {FormMixin} from '../formMixin.js';
import { COUNTY_DATA } from './county-list.js';
import {STATE_CONFIG} from '../ns-state/state-config.js';

/**
 * This is the City/State/Zip Component. Displays a form with inputs for
 * City, State, and Zip.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>City State Zip Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-city-state-zip></ns-city-state-zip>
 *   </template>
 * </demo-snippet>
 */
export class NsCityStateZip extends FormMixin(PolymerElement) {
    static get is() {
        return 'ns-city-state-zip';
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    // properties, observers, etc. are identical to 2.x
    static get properties() {
        return {
            /**
             * Label for county field
             */
            countyLabel: {
                type: String
            },
            /**
             * Placeholder for county field
             */
            countyPlaceholder: {
                type: String
            },
            isCountyDropdown: {
                type: Boolean,
                computed: '_isCountyDropdown(countyList, populateCountiesFromState, enableZipCodeAutocomplete, disableCountyDropdown, useVNRegionListApproach, countyDropdown)'
            },
            /**
             * County field value from user input or passed in
             *
             * Examples:
             *  - 'Utah County'
             */
            county: {
                type: String,
                notify: true,
                observer: '_onCountyChanged'
            },
            /**
             * Error message for invalid county field value
             */
            countyErrorMessage: {
                type: String
            },
            cityDropdown: {
                type: Boolean,
                value: false
            },
            countyDropdown: {
                type: Boolean,
                value: false
            },
            stateDropdown: {
                type: Boolean,
                value: true
            },
            /**
             * Label for city field
             */
            cityLabel: {
                type: String
            },
            /**
             * Placeholder for city field
             */
            cityPlaceholder: {
                type: String
            },
            /**
             * City field value from user input or passed in
             *
             * Examples:
             *  - 'Baltimore'
             */
            city: {
                type: String,
                notify: true
            },
            /**
             * Error message for invalid city field value
             */
            cityErrorMessage: {
                type: String
            },
            /**
             * Label for state field
             */
            stateLabel: {
                type: String
            },
            /**
             * State field value from user input or passed in
             *
             * Examples:
             *  - 'MD', 'WA'
             */
            state: {
                type: String,
                notify: true,
                observer: '_onStateChanged'
            },
            /**
             * Label for zip field
             */
            zipLabel: {
                type: String
            },
            /**
             * Zip field that is bound to those who consume this element
             */
            zip: {
                type: String,
                notify: true,
                observer: '_onZipChanged'
            },
            /**
             * Zip field bound to the value the user is inputting
             *
             * Examples:
             *  - '21202'
             */
            zipHolder: {
                type: String,
            },
            /**
             /**
             * Error message for invalid zip field value
             */
            zipErrorMessage: {
                type: Boolean
            },
            /**
             * Country field value, defaults to 'US'
             *
             * Exampels:
             *  - 'US', 'DE'
             */
            country: {
                type: String,
                value: "US",
                observer: '_initialize'
            },
            /**
             * Language field value, defaults to 'en'
             *
             * Examples:
             *  - 'en', 'es'
             */
            language: {
                type: String,
                value: "en",
                observer: '_initialize'
            },
            /**
             * Flag whether an element is required, default to false
             */
            required: {
                type: Boolean,
                value: false
            },
            /**
             * Language data for this component
             */
            languageData: {
                type: Object
            },
            /**
             * Value for the city selected or typed in
             */
            postalCity: {
                type: String,
                notify: true
            },
            /**
             * Display Order controls the order of fields and whether they show up or not.  If it isn't in the array it wont show up
             * County is hidden by default but a possible array is ['county','city','state','zip']
             */
            displayOrder: {
                type: Array,
                value: [['city', 'state', 'zip'], ['county']]
            },

            /**
             * Enable google auto complete for the address
             */
            enableGoogleAutoComplete: {
                type: Boolean,
                value: false
            },
            /**
             * Flag representing the dirty state of this component
             */
            dirty: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Flag to determine if counties should be populated from state list
             */
            populateCountiesFromState: {
                type: Boolean,
                value: false
            },
            useVNRegionListApproach: {
                type: Boolean,
                value: false
            },
            /**
             * Config for city field not required
             */
            optionalCity: {
                type: Boolean,
                value: false
            },
            /**
             * Config for county field not required
             */
            optionalCounty: {
                type: Boolean,
                value: false
            },
            /**
             * Config for state field not required
             */
            optionalState: {
                type: Boolean,
                value: false
            },
            /**
             * Config for zip field not required
             */
            optionalZip: {
                type: Boolean,
                value: false
            },
            sortStateList: {
                type: Boolean,
                value: true
            },
            /**
             * Config to keep county as textbox even with autocomplete enabled
             */
            disableCountyDropdown: {
                type: Boolean,
                value: false
            },
            vnRegionList: {
                type: Array,
                value: () => []
            },
            /**
             * Translations from the server. Triggers the translation assignment process.
             */
             serverTranslations: {
                type: Object,
                value: function() {
                    return {}
                }
            },
            useServerTranslations: {
                type: Boolean,
                value: false
            }
        };
    }

    static get observers() {
        return [
            '_updateDirtyFlag(cityDirty, stateDirty, countyDirty, countryDirty, zipDirty)',
            '_cleanData(displayOrder, state)'
        ];
    }

    ready() {
        super.ready();
        this._initialize();
    }

    /**
     * Initialize gets called when first loaded or anytime the country or language change
     * @private
     */
    _initialize() {
        if (this.country && this.country.length === 2 && this.language && this.language.length === 2) {
            this._localize(this.language, this.country, CITY_STATE_ZIP_TRANSLATIONS, this.serverTranslations, this.useServerTranslations);
            this._setConfig(this.country, CITY_STATE_ZIP_CONFIG);

            this.cityList = [];
            this.countyList = COUNTY_DATA[this.country] ? COUNTY_DATA[this.country].countyList : [];

            this._setCountyDropdownOptions();
            this._onStateChanged();
        }

        if (this.enableZipCodeAutocomplete) this._onZipChanged();

        if (this.useVNRegionListApproach) this._initVNRegionList();
    }

    _onZipCodeBlur() {
        this.set('zip', this.zipHolder);
    }

    _onZipChanged() {
        if (this.zip !== this.zipHolder) this.set('zipHolder', this.zip);
        if (this.enableZipCodeAutocomplete) this._autocompleteFromZipCode(this.zip).catch(console.error);
    }

    /**
     * Performs a postal code lookup with the given postal code and attempts
     * to autopopulate portions of the form using the lookup results.
     * @param {string} code 
     */
    async _autocompleteFromZipCode(code) {
        const matchingDTOs = await ShippingService.getListOfCitiesFromPostalCode(this.country, code);
        if (matchingDTOs.length > 1) {
            // More than one matching result found; prompt user to select best match
            this._handleMultipleLookupResults(matchingDTOs);
        } else if (matchingDTOs.length === 1) {
            // Only one matching result found; autopopulate fields from result
            this._setAddressFromPostalCodeDTO(matchingDTOs[0]);
        } else {
            // No matching results found; do nothing
            if (this.resetOnNullZipSearch) {
                this.stateList = STATE_CONFIG[this.country].stateList;
            }
        }
    }

    async _initVNRegionList() {
        try {
            this.vnRegionList = await ShippingService.getVNRegionList();
            this.stateList = this.vnRegionList.map(region => ({
                key: region.value,
                value: region.text
            }));
        } catch (e) {
            console.error(e);
        }
    }

    /**
     * Triggered when the State field changes.
     * @private
     */
    _onStateChanged() {
        if (this.populateCountiesFromState) {
            this._setCountyDropdownOptions();
        }

        if (this.useVNRegionListApproach) {
            const region = this.vnRegionList.find(reg => reg.value === this.state);

            if (!region) {
                this.countyList = [];
                return;
            }

            this.countyList = region.districts.map(dist => ({
                key: dist.text,
                value: dist.text
            }));
        }
    }

    _onCountyChanged() {
        if (this.useVNRegionListApproach) {
            const region = this.vnRegionList.find(reg => reg.value === this.state);

            if (!region) {
                this.countyList = [];
                return;
            }

            const district = region.districts.find(dist => dist.text === this.county);

            if (!district) {
                this.cityList = [];
                return;
            }

            this.cityList = district.cities.map(city => ({
                key: city,
                value: city
            }));
        }
    }

    /**
     * Sets the dropdown contents of the County field.
     */
    _setCountyDropdownOptions() {
        // Choose source of dropdown options based on operating mode
        if (this.populateCountiesFromState 
            && STATE_COUNTY[this.country] 
            && STATE_COUNTY[this.country][this.state]) {
            this.countyList = STATE_COUNTY[this.country][this.state]['districts']
                .sort((a, b) => (a > b) ? 1 : (a < b) ? -1 : 0)
                .map(c => ({key: c, value: c}));
        } else if (COUNTY_DATA[this.country]) {
            this.countyList = COUNTY_DATA[this.country].countyList;
        } else {
            this.countyList = [];
        }
    }

    /**
     * Prompts the user to select the best match from the postal code lookup results.
     * @param {PostalCodeLookupDTO[]} dtos - List of lookup results
     */
    _handleMultipleLookupResults(dtos) {
        const regions = [...new Set(dtos.filter(d => d.region).map(d => d.region))];
        const districts = [...new Set(dtos.filter(d => d.district).map(d => d.district))];
        const cities = [...new Set(dtos.filter(d => d.city).map(d => d.city))];

        let requiresDialog = false;
        if (regions.length > 1 && !this.stateDropdown) requiresDialog = true;
        if (districts.length > 1 && !this.isCountyDropdown) requiresDialog = true;
        if (cities.length > 1 && !this.cityDropdown) requiresDialog = true;

        const toDropOption = (str) => ({key: str, value: str});

        if (requiresDialog) {
            this._lookupOptions = dtos;
            const dialog = this.shadowRoot.querySelector('#autocompleteDialog');
            if (!dialog) return;
            dialog.open();
        } else {
            if (this.stateDropdown) this.stateList = regions.map(toDropOption);
            if (regions.length === 1) this.state = regions[0];

            if (this.isCountyDropdown) this.countyList = districts.map(toDropOption);
            if (districts.length === 1) this.county = districts[0];

            if (this.cityDropdown) this.cityList = cities.map(toDropOption);
            if (cities.length === 1) this.city = cities[0];
        }
    }

    /**
     * Triggered when the user selects a lookup result from the selection dialog.
     * @see this._promptSelectLookupResult
     * @param {*} event 
     */
    _onUserSelectLookupResult(event) {
        this._setAddressFromPostalCodeDTO(event.model.item);
        this.shadowRoot.querySelector('#autocompleteDialog').close();
    }

    /**
     * Sets the City, County, State, and/or Zip Code from the given postal code
     * lookup results. Form fields are populated as their respective data is
     * found in the lookup results.
     * @param {PostalCodeLookupDTO} dto - Postal code lookup result object.
     */
    _setAddressFromPostalCodeDTO(dto) {
        if (dto.region) {
            this.state = dto.region;
            this.stateList = [{
                key: dto.region, 
                value: dto.regionDesc || dto.region
            }];
        }
        if (dto.district) {
            this.county = dto.district;
            this.countyList = [{
                key: dto.district, 
                value: dto.districtDesc || dto.district
            }];
        }
        if (dto.city) {
            this.city = dto.city;
            this.cityList = [{
                key: dto.city, 
                value: dto.city
            }];
        }
        if (dto.postalcode) {
            this.zip = dto.postalcode;
        }
    }

    /**
     * Just so we can do a comparison in the html to hide fields
     * @param val1
     * @param val2
     * @returns {boolean}
     * @private
     */
    _isEqualTo(val1, val2) {
        return val1 === val2;
    }

    /**
     * Returns `true` if the County field input type should be a dropdown selector, `false` if 
     * it should be a text input.
     * @param {any[]} countyList - County list
     * @param {boolean} populateCountiesFromState - Flag indicating whether the county field is populated from the State-County map
     * @param {boolean} enableZipCodeAutocomplete - Flag indicating whether zip code autocomplete is enabled for this form
     * @returns {boolean}
     */
    _isCountyDropdown(countyList, populateCountiesFromState, enableZipCodeAutocomplete, disableCountyDropdown, useVNRegionListApproach, countyDropdown) {
        if (countyDropdown) return true;

        if (useVNRegionListApproach) return true;

        return !!(((countyList && countyList.length > 0) || populateCountiesFromState || enableZipCodeAutocomplete) && !disableCountyDropdown);
    }

    /**
     * Form validation - ns-form looks for this function for validation
     */
    validate() {
        return this.shadowRoot.querySelector("ns-form").validate();
    }

    _cleanData(displayOrder, state) {
        if (state && state != '') {
            let hasState = false;
            if (displayOrder.includes("state")) {
                hasState = true;
            } else {
                hasState = displayOrder.some(arr => arr.includes("state"))
            }
            if (!hasState) {
                this.state = ''
            }
            
        }
    }
}

customElements.define(NsCityStateZip.is, NsCityStateZip);
